import IntelligentContentParser from './IntelligentContentParser';
import { isShieldLikeEntry } from './CharacterSheetWeaponPresentation';
import { isAmmunitionEntry, isMagicEquipmentEntry } from './EquipmentState';

const SIMPLE_WEAPON_LABELS = new Set([
  'club',
  'dagger',
  'dart',
  'greatclub',
  'handaxe',
  'javelin',
  'light crossbow',
  'light hammer',
  'mace',
  'quarterstaff',
  'shortbow',
  'sickle',
  'sling',
  'spear'
]);

const dedupeStrings = (values) => {
  const seen = new Set();
  const deduped = [];

  values.forEach((value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });

  return deduped;
};

const normalizeDisplayText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const canonicalItemDisplayName = (value) => String(value || '')
  .trim()
  .replace(/\bGlamored Studded Leather(?: Armor)?\b/gi, 'Glamoured Studded Leather Armor');

const toNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/-?\d+/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return fallback;
};

const isEquipmentObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const getEquipmentObject = (character) => (
  isEquipmentObject(character?.equipment)
    ? character.equipment
    : {}
);

const collectInventoryEntries = (character) => {
  const equipment = getEquipmentObject(character);
  const inventoryEquipment = isEquipmentObject(character?.inventory?.equipment)
    ? character.inventory.equipment
    : {};
  const describe = (entries, sourceCollection) => (Array.isArray(entries)
    ? entries.map((entry, sourceIndex) => ({ entry, sourceCollection, sourceIndex }))
    : []);
  const nonEquippedWeaponEntries = [
    ...describe(equipment?.weapons, 'equipment.weapons'),
    ...describe(character?.weapons, 'weapons'),
    ...describe(inventoryEquipment?.weapons, 'inventory.equipment.weapons')
  ].filter(({ entry }) => (
    isShieldLikeEntry(entry)
    || isAmmunitionEntry(entry)
    || isMagicEquipmentEntry(entry)
  ));

  return [
    ...describe(equipment?.armor, 'equipment.armor'),
    ...describe(equipment?.items, 'equipment.items'),
    ...describe(equipment?.equipment, 'equipment.equipment'),
    ...describe(equipment?.other, 'equipment.other'),
    ...nonEquippedWeaponEntries,
    ...describe(character?.items, 'items'),
    ...describe(Array.isArray(character?.inventory?.equipment) ? character.inventory.equipment : [], 'inventory.equipment'),
    ...describe(Array.isArray(character?.equipment) ? character.equipment : [], 'equipment')
  ].filter(({ entry }) => Boolean(entry));
};

const collectValuables = (character) => {
  const equipment = getEquipmentObject(character);
  const merged = [
    ...(Array.isArray(equipment?.valuables) ? equipment.valuables : []),
    ...(Array.isArray(character?.wealth?.valuables) ? character.wealth.valuables : []),
    ...(Array.isArray(character?.gems) ? character.gems : []),
    ...(Array.isArray(character?.treasure) ? character.treasure : [])
  ].filter(Boolean);

  return merged.map((entry, index) => {
    if (typeof entry === 'string') {
      return {
        key: `valuable-${index}-${entry.toLowerCase()}`,
        name: entry,
        quantity: 1
      };
    }

    const name = String(entry?.name || entry?.title || '').trim();
    if (!name) return null;

    return {
      key: `valuable-${index}-${name.toLowerCase()}`,
      name,
      quantity: typeof entry?.quantity === 'number' && Number.isFinite(entry.quantity)
        ? entry.quantity
        : 1
    };
  }).filter(Boolean);
};

const getCurrencyValue = (source, shortKey, longKey, fallback = 0) => (
  toNumber(
    source?.[shortKey]
    ?? source?.[longKey]
    ?? fallback,
    fallback
  )
);

const getCharacterClassNames = (character) => {
  const rawClassNames = [
    character?.class,
    ...(Array.isArray(character?.classes)
      ? character.classes.flatMap((entry) => [entry?.name, entry?.className, entry?.class])
      : [])
  ];

  return dedupeStrings(rawClassNames);
};

const mergeSrdClassProficiencies = (classNames) => {
  const buckets = {
    armor: [],
    weapons: [],
    tools: [],
    savingThrows: []
  };

  classNames.forEach((className, index) => {
    const srd = IntelligentContentParser.getSRDClassProficiencies(className);
    if (!srd) return;

    buckets.armor.push(...(srd.armor || []));
    buckets.weapons.push(...(srd.weapons || []));
    buckets.tools.push(...(srd.tools || []));
    if (index === 0 && Array.isArray(srd.savingThrows)) {
      buckets.savingThrows.push(...srd.savingThrows);
    }
  });

  return {
    armor: dedupeStrings(buckets.armor),
    weapons: dedupeStrings(buckets.weapons),
    tools: dedupeStrings(buckets.tools),
    savingThrows: dedupeStrings(buckets.savingThrows)
  };
};

export const buildCharacterInventoryRows = (character) => {
  const rows = [];
  const seen = new Set();

  collectInventoryEntries(character).forEach(({ entry, sourceCollection, sourceIndex }) => {
    const normalizedEntry = typeof entry === 'string'
      ? { name: entry }
      : entry;
    if (!normalizedEntry) return;
    const typeLabel = String(
      normalizedEntry?.type
      || normalizedEntry?.kind
      || normalizedEntry?.category
      || normalizedEntry?.itemType
      || ''
    ).trim().toLowerCase();
    if (typeLabel === 'valuable' || typeLabel === 'currency') return;

    const name = canonicalItemDisplayName(normalizedEntry?.name || normalizedEntry?.title);
    if (!name) return;

    const quantity = typeof normalizedEntry?.quantity === 'number' && Number.isFinite(normalizedEntry.quantity)
      ? normalizedEntry.quantity
      : 1;
    const weight = normalizedEntry?.weight ?? normalizedEntry?.encumbrance ?? '';
    // date_of_change: 2026-07-29
    // rule_reference: SmartPaste may provide item properties/effects separately from description;
    // the first-class magic-item editor must still expose that rules text for review.
    const propertyDescription = Array.isArray(normalizedEntry?.properties)
      ? normalizedEntry.properties.filter(Boolean).join('\n')
      : '';
    const effectDescription = Array.isArray(normalizedEntry?.effects)
      ? normalizedEntry.effects
        .map((effect) => typeof effect === 'string' ? effect : effect?.description)
        .filter(Boolean)
        .join('\n')
      : '';
    const description = String(
      normalizedEntry?.description
      || normalizedEntry?.effect
      || normalizedEntry?.text
      || propertyDescription
      || effectDescription
      || ''
    ).trim();
    const requiresAttunement = normalizedEntry?.requiresAttunement === true
      || normalizedEntry?.attunement === true;
    const magical = isMagicEquipmentEntry(normalizedEntry);
    const fingerprint = `${name.toLowerCase()}|${quantity}|${String(weight).trim().toLowerCase()}|${typeLabel}`;
    if (seen.has(fingerprint)) return;
    seen.add(fingerprint);

    rows.push({
      key: `${sourceCollection}-${sourceIndex}`,
      name,
      quantity,
      weight,
      type: typeLabel || 'item',
      description,
      rarity: String(normalizedEntry?.rarity || '').trim().toLowerCase(),
      magical,
      equipped: normalizedEntry?.equipped === true,
      requiresAttunement,
      attuned: normalizedEntry?.attuned === true,
      printLocation: String(
        normalizedEntry?.printLocation
        || normalizedEntry?.equippedLocation
        || normalizedEntry?.equipmentSlot
        || ''
      ).trim().toLowerCase(),
      properties: Array.isArray(normalizedEntry?.properties) ? normalizedEntry.properties.map(String) : [],
      sourceCollection,
      sourceIndex
    });
  });

  return rows;
};

export const buildCharacterWealthSummary = (character) => {
  const equipment = getEquipmentObject(character);
  const currencySource = equipment?.currency
    || character?.currency
    || character?.coins
    || character?.wealth?.currency
    || {};

  const coins = {
    cp: getCurrencyValue(currencySource, 'cp', 'copper', character?.copper ?? 0),
    sp: getCurrencyValue(currencySource, 'sp', 'silver', character?.silver ?? 0),
    ep: getCurrencyValue(currencySource, 'ep', 'electrum', character?.electrum ?? 0),
    gp: getCurrencyValue(currencySource, 'gp', 'gold', character?.gold ?? 0),
    pp: getCurrencyValue(currencySource, 'pp', 'platinum', character?.platinum ?? 0)
  };

  return {
    coins,
    valuables: collectValuables(character)
  };
};

export const buildCharacterProficiencySummary = (character) => {
  const proficiencies = isEquipmentObject(character?.proficiencies)
    ? character.proficiencies
    : {};
  const classNames = getCharacterClassNames(character);
  const srdProficiencies = mergeSrdClassProficiencies(classNames);
  const speciesName = character?.species || character?.race;
  const srdLanguages = speciesName
    ? IntelligentContentParser.getSRDSpeciesLanguages(speciesName)
    : [];

  // Explicit proficiency arrays are the player's editable record. Only infer from SRD
  // class/species data when that record is absent; otherwise an unchecked proficiency
  // could never stay unchecked. date_of_change: 2026-08-08.
  const armorLabels = dedupeStrings(Array.isArray(proficiencies.armor)
    ? proficiencies.armor
    : srdProficiencies.armor);
  const weaponLabels = dedupeStrings(Array.isArray(proficiencies.weapons)
    ? proficiencies.weapons
    : srdProficiencies.weapons);
  const toolLabels = dedupeStrings(Array.isArray(character?.toolProficiencies)
    ? character.toolProficiencies
    : (Array.isArray(proficiencies.tools) ? proficiencies.tools : srdProficiencies.tools));
  const languageLabels = dedupeStrings(Array.isArray(character?.languages)
    ? character.languages
    : (Array.isArray(proficiencies.languages) ? proficiencies.languages : srdLanguages));

  const hasArmorLabel = (needle) => armorLabels.some((label) => label.toLowerCase().includes(needle));
  const hasWeaponLabel = (needle) => weaponLabels.some((label) => label.toLowerCase().includes(needle));

  return {
    armor: {
      labels: armorLabels,
      light: hasArmorLabel('light'),
      medium: hasArmorLabel('medium'),
      heavy: hasArmorLabel('heavy'),
      shields: hasArmorLabel('shield')
    },
    weapons: {
      labels: weaponLabels,
      simple: hasWeaponLabel('simple'),
      martial: hasWeaponLabel('martial'),
      specific: weaponLabels.filter((label) => {
        const normalized = label.toLowerCase();
        if (normalized.includes('simple') || normalized.includes('martial')) return false;
        if (SIMPLE_WEAPON_LABELS.has(normalized) && hasWeaponLabel('simple')) return false;
        return true;
      })
    },
    tools: toolLabels,
    languages: languageLabels
  };
};

export const hasDistinctStructuredNotes = (entries, notesText) => {
  const normalizedNotes = normalizeDisplayText(notesText);
  if (!normalizedNotes) return false;

  const structuredText = Array.isArray(entries)
    ? entries
      .map((entry) => {
        const name = String(entry?.name || '').trim();
        const description = String(entry?.description || entry?.text || entry?.content || '').trim();
        return `${name}${description ? `: ${description}` : ''}`;
      })
      .filter(Boolean)
      .join('\n')
    : '';

  const normalizedStructured = normalizeDisplayText(structuredText);
  if (!normalizedStructured) return true;
  if (normalizedNotes === normalizedStructured) return false;

  const noteFragments = String(notesText || '')
    .split(/\n+/)
    .flatMap((line) => line.split(/[;]+/))
    .map(normalizeDisplayText)
    .filter(Boolean);

  if (noteFragments.length > 0 && noteFragments.every((fragment) => normalizedStructured.includes(fragment))) {
    return false;
  }

  return true;
};

const CharacterSheetReferencePresentation = {
  buildCharacterInventoryRows,
  buildCharacterWealthSummary,
  buildCharacterProficiencySummary,
  hasDistinctStructuredNotes
};

export default CharacterSheetReferencePresentation;
